import { useCallback, useMemo } from 'react';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { FIELD_NAMES } from '../../../../../../../address/address-fields/constants';
import { updateFulfillmentPlanAddress } from '../../../../../../../fulfillment-plans/fulfillment-plans.api';
import { useUpdateEntitiesFulfillmentPlans, useUpdateCswWithEntity } from '../../../../../hooks/use-update-entities-fulfillment-plans';
import { useGetUpdatableFulfillmentPlanIds } from '../use-shipment-details-address';
import { cleanedEmailString } from '../../../../../../../address/address-fields/helpers';

const getRecipientInfo = (model) => ({
  [FIELD_NAMES.NAME]: model[FIELD_NAMES.NAME],
  [FIELD_NAMES.PHONE]: model[FIELD_NAMES.PHONE],
  [FIELD_NAMES.EMAIL]: cleanedEmailString(model[FIELD_NAMES.EMAIL]),
});

export const useSaveShipToForm = ({ fulfillmentPlanId, salesOrderId, shipToForm, shouldLock, isBulkUpdate }) => {
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const [apiError, setApiError] = useAsyncSafeState(null);
  const bulkFulfillmentPlanIds = useGetUpdatableFulfillmentPlanIds(salesOrderId);
  const clearApiError = useCallback(() => setApiError(null), [setApiError]);
  const fulfillmentPlanIds = useMemo(
    () => (isBulkUpdate ? bulkFulfillmentPlanIds : [fulfillmentPlanId]),
    [isBulkUpdate, bulkFulfillmentPlanIds, fulfillmentPlanId],
  );

  const payload = useMemo(
    () => ({
      fulfillmentPlanIds,
      shipTo: {
        ...shipToForm,
        email: cleanedEmailString(shipToForm.email),
        lockAddress: shouldLock,
      },
      validateAddress: false,
      recipientInfo: getRecipientInfo(shipToForm),
    }),
    [shipToForm, fulfillmentPlanIds, shouldLock],
  );

  const updateEntities = useUpdateEntitiesFulfillmentPlans();
  const updateCswWithEntity = useUpdateCswWithEntity();

  const save = useCallback(async () => {
    setIsSaving(true);

    try {
      const { data } = await updateFulfillmentPlanAddress(payload);
      const fulfillmentPlan = data?.find((item) => item.fulfillmentPlanId === fulfillmentPlanId) || {};
      updateCswWithEntity(fulfillmentPlan);
      updateEntities(data);
      return true; // didSucceed
    } catch (err) {
      const errorMessage = err.response?.data?.Errors?.[0]?.Message;
      setApiError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [setIsSaving, payload, updateCswWithEntity, updateEntities, fulfillmentPlanId, setApiError]);

  const value = useMemo(
    () => ({
      apiError,
      clearApiError,
      isSaving,
      save,
    }),
    [clearApiError, apiError, isSaving, save],
  );

  return value;
};
